/**
 * List and view state in the URL (TMM-B6, NAV-009 / NAV-010 / NAV-011): filters, search, page and
 * tab live in the query string so a reload, a shared link and Back all keep the view.
 *
 * `useUrlState(schema)` is typed by a schema of codecs, one per key. Reading is total: an absent
 * or unreadable value is the key's default, never an error. Writing is `set(patch, { push? })`:
 * `replace` by default (a filter or a search must not stack history), `push` only for a tab
 * change. A value equal to its default is removed from the URL, and every param the schema does
 * not name is left alone, so pages and the tabs nested inside them share one query string.
 *
 * View state only: never put a token, a credential or a tenant identifier in a schema.
 */
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BACKGROUND_WRITE_STATE } from './unsaved-guard-context';

/** One URL param: how to read it, how to write it, and what it is when absent. */
export interface Codec<T> {
  readonly default: T;
  /** The value for a present param; an unreadable one is `default`. Never throws. */
  parse(raw: string): T;
  /** The param text for `value`. Two values that format the same are the same value. */
  format(value: T): string;
}

export type UrlSchema<S> = { [K in keyof S]: Codec<S[K]> & { default: S[K] } };

export interface UrlStateOptions<S> {
  /**
   * Keys (typically `['page']`) put back to their default whenever any OTHER key changes, unless
   * the same patch sets them. A patch that leaves every other key as it is does not reset them.
   */
  resetOnChange?: readonly (keyof S)[];
}

export interface SetOptions {
  /** Push a history entry (a tab change). Everything else replaces the current one. */
  push?: boolean;
}

export type UrlStateSetter<S> = (patch: Partial<S>, options?: SetOptions) => void;

const CIVIL = /^\d{4}-\d{2}-\d{2}$/;
const INTEGER = /^-?\d+$/;
const CIVIL_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** A free string, as received. */
export function stringParam(def = ''): Codec<string> {
  return { default: def, parse: (raw) => raw, format: (value) => value };
}

/** One of `values`; anything else (including a stale or hand-edited link) is `def`. */
export function enumParam<V extends string>(values: readonly V[], def: V): Codec<V> {
  return {
    default: def,
    parse: (raw) => values.find((value) => value === raw) ?? def,
    format: (value) => value,
  };
}

/** One of a closed set of numbers (a horizon of 3 / 6 / 12 months); anything else is `def`. */
export function numberEnumParam<V extends number>(values: readonly V[], def: V): Codec<V> {
  return {
    default: def,
    parse: (raw) => values.find((value) => String(value) === raw) ?? def,
    format: (value) => String(value),
  };
}

/** A whole number within `[min, max]`; garbage, NaN, a fraction or an out-of-range value is `def`. */
export function intParam(def: number, range: { min?: number; max?: number } = {}): Codec<number> {
  const { min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER } = range;
  return {
    default: def,
    parse: (raw) => {
      if (!INTEGER.test(raw)) return def;
      const value = Number(raw);
      return Number.isSafeInteger(value) && value >= min && value <= max ? value : def;
    },
    format: (value) => String(value),
  };
}

/**
 * A civil date (`YYYY-MM-DD`), kept exactly as received: never parsed through `Date`, so no zone
 * can shift it. Anything that is not that shape is `def`. With `allowEmpty`, an empty param is a
 * real value (a date field the person cleared) rather than the default.
 */
export function civilDateParam(def = '', options: { allowEmpty?: boolean } = {}): Codec<string> {
  return {
    default: def,
    parse: (raw) => (CIVIL.test(raw) || (options.allowEmpty === true && raw === '') ? raw : def),
    format: (value) => value,
  };
}

/** `format(null)`: never the text of a real param, so a `null` value can only mean "no param". */
const ABSENT_TEXT = '\u0000';

/**
 * A free string with THREE states (TMMAPP-0081): absent (`null`, the default), empty (`''`, a
 * param written as `?key=`) and any text, all kept as received. For a param whose absence means
 * something different from its being empty (the virtual account register's `from` / `to`: absent is the
 * default month, empty on both is "all dates"). Writing `null` removes the param.
 */
export function optionalStringParam(): Codec<string | null> {
  return { default: null, parse: (raw) => raw, format: (value) => value ?? ABSENT_TEXT };
}

/** One of `values`, or `null` (the default) for an absent or unknown param — "not chosen yet". Writing `null` removes it. */
export function optionalEnumParam<V extends string>(values: readonly V[]): Codec<V | null> {
  return {
    default: null,
    parse: (raw) => values.find((value) => value === raw) ?? null,
    format: (value) => value ?? ABSENT_TEXT,
  };
}

/** A calendar month `YYYY-MM` (month 01–12), kept as received; anything else is `def`. */
export function civilMonthParam(def: string): Codec<string> {
  return {
    default: def,
    parse: (raw) => (CIVIL_MONTH.test(raw) ? raw : def),
    format: (value) => value,
  };
}

/** A flag, written `1`; `1` / `true` read as on, `0` / `false` as off, anything else is `def`. */
export function booleanParam(def = false): Codec<boolean> {
  return {
    default: def,
    parse: (raw) => {
      if (raw === '1' || raw === 'true') return true;
      if (raw === '0' || raw === 'false') return false;
      return def;
    },
    format: (value) => (value ? '1' : '0'),
  };
}

/**
 * The query the last `set` asked for, kept for the rest of that tick so a second `set` (from this
 * hook or another instance) builds on it rather than on the render the router has not yet replaced.
 */
let pending: { from: string; next: URLSearchParams } | null = null;

/**
 * Typed URL state over the router's query string: `[state, set]`. `set` is stable, and several
 * calls in one tick merge, each starting from the query the previous one produced (react-router's
 * own `setSearchParams` would start every one of them from the same stale render).
 *
 * Declare the schema at module level (or memoize it) so `state` keeps its identity between
 * renders; the hook is correct either way.
 */
export function useUrlState<S extends object>(
  schema: UrlSchema<S>,
  options: UrlStateOptions<S> = {},
): [S, UrlStateSetter<S>] {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const state = useMemo(() => {
    const out = {} as S;
    for (const key of Object.keys(schema) as (keyof S & string)[]) {
      const codec = schema[key];
      const raw = searchParams.get(key);
      out[key] = raw === null ? codec.default : codec.parse(raw);
    }
    return out;
  }, [schema, searchParams]);

  const latest = useRef({ schema, options, searchParams, pathname, navigate });
  useLayoutEffect(() => {
    latest.current = { schema, options, searchParams, pathname, navigate };
  });

  const set = useCallback<UrlStateSetter<S>>((patch, setOptions) => {
    const {
      schema: current,
      options: opts,
      searchParams: rendered,
      pathname: path,
    } = latest.current;
    const renderedKey = `${path}?${rendered.toString()}`;
    const base = pending !== null && pending.from === renderedKey ? pending.next : rendered;
    const next = new URLSearchParams(base);
    const written = Object.keys(patch) as (keyof S & string)[];
    let otherChanged = false;

    for (const key of written) {
      const value = patch[key];
      const codec = current[key];
      if (value === undefined) continue;
      const text = codec.format(value as S[typeof key]);
      const before = next.get(key) ?? codec.format(codec.default);
      if (text === codec.format(codec.default)) next.delete(key);
      else next.set(key, text);
      if (text !== before && !(opts.resetOnChange ?? []).includes(key)) otherChanged = true;
    }

    if (otherChanged) {
      for (const key of (opts.resetOnChange ?? []) as (keyof S & string)[]) {
        if (!written.includes(key)) next.delete(key);
      }
    }

    if (next.toString() === base.toString()) return;
    pending = { from: renderedKey, next };
    queueMicrotask(() => {
      if (pending?.next === next) pending = null;
    });
    // The view changes in place, so the page keeps its scroll position (`ScrollRestoration` would
    // otherwise treat the new location as a new page and scroll to the top).
    // A replace is the page writing its own URL (a debounced search, a stale filter cleaned up on
    // load), never the person leaving: it carries the marker so a dirty editor's guard lets it
    // through. A push is a tab or a step the person chose, and is held like any navigation.
    const replace = setOptions?.push !== true;
    void latest.current.navigate(`?${next.toString()}`, {
      replace,
      preventScrollReset: true,
      ...(replace ? { state: BACKGROUND_WRITE_STATE } : {}),
    });
  }, []);

  return [state, set];
}

/** `keys` (default: every key) of `schema` at their defaults: a patch that clears them. */
export function defaultsOf<S extends object>(
  schema: UrlSchema<S>,
  keys: readonly (keyof S)[] = Object.keys(schema) as (keyof S)[],
): Partial<S> {
  const out: Partial<S> = {};
  for (const key of keys) out[key] = schema[key].default;
  return out;
}

/** How many of `keys` are away from their default: the active filters (TBL-005). */
export function activeCount<S extends object>(
  schema: UrlSchema<S>,
  state: S,
  keys: readonly (keyof S)[],
): number {
  return keys.filter((key) => {
    const codec = schema[key];
    return codec.format(state[key]) !== codec.format(codec.default);
  }).length;
}
